import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import ReactModal from 'react-modal';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useTemplate } from '../../../../queries';
import { useAutomationTemplateStepHtmlNewsletterUpdateMutation } from '../../../../queries/AutomationTemplate/useAutomationTemplateStepHtmlNewsletterUpdateMutation';
import { baseStyleSheet } from '../../../styles/styles';
import { CloseButton } from '../../CloseButton';
import { NewsletterTemplateSelectorButton } from '../../HtmlNewsletter/NewsletterTemplateSelectorButton';
import HtmlNewsletterCampaignComposer from '../../HtmlNewsletterCampaignComposer';
import { LoadingSpinner } from '../../LoadingSpinner';
import { TextInput } from '../../TextInput';
import { styleSheet } from './styles';

interface Props {
	automationTemplateId: string;
	step: Api.IHtmlNewsletterAutomationStep;
	className?: string;
	onRequestRemoveSavedEmailAttachment?(savedAttachment: Api.IFileAttachment): Promise<any>;
	readonly?: boolean;
	styles?: StyleDeclarationValue[];
	onRequestClose?(result?: Api.IHtmlNewsletterAutomationStep, canceled?: boolean): void;
	syncSubjectWithStep?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	onStepChanged?: () => void;
}

export function PreviewAutomationStepNewsletterModal({
	automationTemplateId,
	step,
	readonly,
	onRequestClose,
	impersonationContext,
	onStepChanged,
}: Props) {
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging();
	const [newsletterTemplate, setNewsletterTemplate] = React.useState<Api.ITemplate>(
		step.options?.templateReference?.templateId
			? {
					id: step.options.templateReference.templateId,
					name: step.options.templateReference.name,
				}
			: null
	);
	const [subject, setSubject] = React.useState(step.options.subject ?? step.subject);
	const [subjectFieldError, setSubjectFieldError] = React.useState<string>();

	const templateQuery = useTemplate<Api.ITemplate>({
		refetchOnWindowFocus: false,
		templateId: newsletterTemplate?.id,
		impersonationContext,
	});

	const onEmailMessageTemplateChanged = (template: Api.ITemplate) => {
		setNewsletterTemplate(template);
		if (template) {
			setSubject(template.subject || template.name || '');
		}
	};

	const onCancel = () => {
		if (!newsletterTemplate?.id) {
			onRequestClose(null, true);
		}
	};

	const onSubjectChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSubject(e.target.value);
		setSubjectFieldError('');
	};

	const onCancelButtonClicked = () => {
		onRequestClose(null, true);
	};
	const newsletterStepUpdateMutation = useAutomationTemplateStepHtmlNewsletterUpdateMutation({
		onError: error => {
			logApiError('SaveNewsletterAutomationStep-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: data => {
			onRequestClose(data, false);
		},
	});
	const saving = newsletterStepUpdateMutation.isPending;

	const onSaveButtonClicked = async () => {
		if (!subject) {
			setSubjectFieldError('Please enter a subject before saving.');
			return;
		}

		const newTemplateReference: Api.ITemplateReference | null = newsletterTemplate?.id
			? {
					isCustomized: step.options?.templateReference?.templateId !== newsletterTemplate.id,
					isSystemTemplate:
						newsletterTemplate?.scope === Api.TemplateScope.System ||
						newsletterTemplate?.scope === Api.TemplateScope.Industry,
					name: newsletterTemplate.name,
					templateId: newsletterTemplate.id,
				}
			: null;
		const newStep = {
			...step,
			content: templateQuery.data.content,
			options: {
				subject,
				templateReference: newTemplateReference,
			},
		};
		newsletterStepUpdateMutation.mutate({
			templateId: automationTemplateId,
			stepId: step.id,
			impersonationContext,
			step: newStep,
		});
		onStepChanged?.();
	};

	const subjectLabel = (
		<span>
			Subject:
			<span className={css(baseStyleSheet.required)}>*</span>
			&nbsp;
		</span>
	);
	return (
		<ReactModal
			onRequestClose={() => onRequestClose(null, true)}
			isOpen={true}
			className={css(styleSheet.modalContent)}
			portalClassName={css(styleSheet.modalRoot)}
			overlayClassName={css(styleSheet.modalOverlay)}
			shouldCloseOnOverlayClick={false}
		>
			<div className={css(styleSheet.modalHeader)}>
				<h2 className={css(styleSheet.modalHeaderTitle)}>Edit Email</h2>
				<CloseButton onClick={() => onRequestClose(null, true)} fillColor='white' />
			</div>
			<div className={css(styleSheet.modalBody)}>
				<div className={css(styleSheet.leftContainer)}>
					<h3 className={css(styleSheet.header)}>Newsletter Template</h3>
					<div className={css(styleSheet.body)}>
						<div className={css(styleSheet.bodyInputs)}>
							<TextInput
								disabled={readonly}
								inputId={`html-newslettter-composer-subject`}
								leftAccessory={subjectLabel}
								onChange={onSubjectChanged}
								type='text'
								value={subject}
							/>
							{Boolean(subjectFieldError) && <div className={css(styleSheet.fieldError)}>{subjectFieldError}</div>}
							<HtmlNewsletterCampaignComposer
								isForAutomation
								hideSubject
								readonly={readonly}
								template={templateQuery.data}
							/>
						</div>
						{!!saving && <LoadingSpinner type='large' className={css(baseStyleSheet.absoluteCenter)} />}
					</div>
					<div className={css(styleSheet.emailComposerTemplatesSelectorButton)}>
						<NewsletterTemplateSelectorButton
							initialTemplateId={step.options?.templateReference?.templateId}
							onTemplateSelected={onEmailMessageTemplateChanged}
							onCancel={onCancel}
						/>
					</div>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.footer)}>
						<button
							className={css(baseStyleSheet.ctaButton)}
							onClick={!readonly ? onSaveButtonClicked : onCancelButtonClicked}
						>
							<span>{!readonly ? 'Save' : 'Close'}</span>
						</button>
						{!readonly ? (
							<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onCancelButtonClicked}>
								<span>Cancel</span>
							</button>
						) : null}
					</div>
				</div>
			</div>
		</ReactModal>
	);
}
